import { apiService, type Collection, type Request } from '../services/apiService';
import { APPLY_ENV_SCOPE_ALL, type ApplyEnvScopeId } from './agentApplyEnvUtils';

export type AgentCollectionOption = {
  id: number;
  name: string;
  parentCollectionId: number | null;
  /** Depth 0 = root; used for indentation in menus. */
  depth: number;
  /** Display label with hierarchy (e.g. "API / Users"). */
  label: string;
};

export type AgentRequestOption = {
  id: number;
  name: string;
  method: string;
  collectionId: number;
  /** Environment assigned on the collection request, if any. */
  environmentId: number | '';
  /** Custom/edited environment URL saved on the collection request. */
  environmentUrl: string;
};

function toPositiveId(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return null;
}

/** Normalize collection API rows so Select values match number ids. */
export function mapCollectionsForAgentMenus(
  collections: Array<Pick<Collection, 'id' | 'name' | 'parentCollectionId'> | null | undefined> | null | undefined,
): AgentCollectionOption[] {
  const rows = (collections ?? [])
    .map((c) => {
      if (!c) return null;
      const id = toPositiveId(c.id);
      if (id == null) return null;
      const parentRaw = c.parentCollectionId as unknown;
      const parentCollectionId =
        parentRaw == null || parentRaw === '' ? null : toPositiveId(parentRaw);
      return {
        id,
        name: (c.name || '').trim() || `Collection #${id}`,
        parentCollectionId: parentCollectionId === id ? null : parentCollectionId,
      };
    })
    .filter((c): c is { id: number; name: string; parentCollectionId: number | null } => c != null);

  const byId = new Map(rows.map((r) => [r.id, r]));
  const children = new Map<number | null, typeof rows>();
  for (const row of rows) {
    const parent =
      row.parentCollectionId != null && byId.has(row.parentCollectionId)
        ? row.parentCollectionId
        : null;
    const list = children.get(parent) ?? [];
    list.push(row);
    children.set(parent, list);
  }
  for (const list of Array.from(children.values())) {
    list.sort((a, b) => a.name.localeCompare(b.name));
  }

  const out: AgentCollectionOption[] = [];
  const visit = (parentId: number | null, depth: number, path: string[]) => {
    for (const row of children.get(parentId) ?? []) {
      const nextPath = [...path, row.name];
      out.push({
        id: row.id,
        name: row.name,
        parentCollectionId: row.parentCollectionId,
        depth,
        label: nextPath.join(' / '),
      });
      visit(row.id, depth + 1, nextPath);
    }
  };
  visit(null, 0, []);
  return out;
}

export function collectCollectionIdsIncludingDescendants(
  rootId: number,
  collections: Array<Pick<AgentCollectionOption, 'id' | 'parentCollectionId'>>,
): number[] {
  const root = toPositiveId(rootId);
  if (root == null) return [];
  const children = new Map<number, number[]>();
  for (const c of collections) {
    const id = toPositiveId(c.id);
    if (id == null) continue;
    const parent = c.parentCollectionId == null ? null : toPositiveId(c.parentCollectionId);
    if (parent == null) continue;
    const list = children.get(parent) ?? [];
    list.push(id);
    children.set(parent, list);
  }
  const out: number[] = [];
  const stack = [root];
  const seen = new Set<number>();
  while (stack.length > 0) {
    const id = stack.pop()!;
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(id);
    for (const child of children.get(id) ?? []) {
      stack.push(child);
    }
  }
  return out;
}

export function resolveAgentRequestEnvironmentId(
  request:
    | {
        environmentId?: number | '' | null;
        environment?: { id?: number | '' | null } | null;
      }
    | null
    | undefined,
): number | '' {
  if (!request) return '';
  const fromId = toPositiveId(request.environmentId);
  if (fromId != null) return fromId;
  const nested = toPositiveId(request.environment?.id);
  if (nested != null) return nested;
  return '';
}

export function resolveAgentRequestEnvironmentUrl(
  request: { environmentUrl?: string | null } | null | undefined,
): string {
  if (typeof request?.environmentUrl !== 'string') return '';
  return request.environmentUrl.trim();
}

function normalizeAgentEnvironmentUrl(url: string | null | undefined): string {
  if (typeof url !== 'string') return '';
  return url.trim().replace(/\/+$/, '');
}

export function matchEnvironmentIdByUrl(
  url: string | null | undefined,
  environments: Array<{ id?: number; url?: string; baseUrl?: string } | null | undefined>,
): number | '' {
  const target = normalizeAgentEnvironmentUrl(url);
  if (!target) return '';
  for (const env of environments) {
    if (!env) continue;
    const id = toPositiveId(env.id);
    if (id == null) continue;
    const candidate =
      normalizeAgentEnvironmentUrl(env.url) || normalizeAgentEnvironmentUrl(env.baseUrl);
    if (candidate && candidate === target) return id;
  }
  return '';
}

export type AgentExecuteEnvironment = {
  environmentId: number | '';
  environmentUrl: string;
};

/** Use the collection request's current environment (id and/or edited URL). */
export function resolveAgentExecuteEnvironment(
  request:
    | {
        environmentId?: number | '' | null;
        environment?: { id?: number | '' | null } | null;
        environmentUrl?: string | null;
      }
    | null
    | undefined,
  environments: Array<{ id?: number; url?: string; baseUrl?: string } | null | undefined> = [],
): AgentExecuteEnvironment {
  const environmentUrl = resolveAgentRequestEnvironmentUrl(request);
  let environmentId = resolveAgentRequestEnvironmentId(request);
  if (environmentId === '' && environmentUrl) {
    environmentId = matchEnvironmentIdByUrl(environmentUrl, environments);
  }
  if (environmentId === '' && environmentUrl) {
    const first = environments.find((env) => toPositiveId(env?.id) != null);
    const firstId = toPositiveId(first?.id);
    if (firstId != null) environmentId = firstId;
  }
  return { environmentId, environmentUrl };
}

export function mapAgentRequestOptions(
  requests: Array<
    | (Pick<Request, 'id' | 'name' | 'method' | 'collectionId'> &
        Partial<Pick<Request, 'environmentId' | 'environment' | 'environmentUrl'>>)
    | null
    | undefined
  > | null | undefined,
  options?: {
    methods?: Set<string>;
    /** When listing All Collections, prefix with folder name for clarity. */
    collectionLabelById?: Map<number, string>;
    prefixCollectionName?: boolean;
  },
): AgentRequestOption[] {
  const methods = options?.methods;
  const labels = options?.collectionLabelById;
  const prefixCollectionName = Boolean(options?.prefixCollectionName);
  return (requests ?? [])
    .map((r) => {
      if (!r) return null;
      const id = toPositiveId(r.id);
      if (id == null) return null;
      const method = String(r.method || '').toUpperCase();
      if (methods && !methods.has(method)) return null;
      const collectionId = toPositiveId(r.collectionId) ?? 0;
      const baseName = `${(r.name || '').trim() || `Request #${id}`} (${method || 'UNKNOWN'})`;
      const folder =
        prefixCollectionName && collectionId > 0 && labels?.has(collectionId)
          ? labels.get(collectionId)
          : null;
      const name = folder ? `${folder} / ${baseName}` : baseName;
      return {
        id,
        name,
        method,
        collectionId,
        environmentId: resolveAgentRequestEnvironmentId(r),
        environmentUrl: resolveAgentRequestEnvironmentUrl(r),
      };
    })
    .filter((r): r is AgentRequestOption => r != null)
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Load requests for All Collections (whole project) or exactly one selected collection
 * (not nested sub-collections — pick those folders separately in the Collection dropdown).
 */
export async function fetchRequestsForAgentCollectionScope(
  projectId: number,
  collectionId: ApplyEnvScopeId | '',
  collections: Array<Pick<AgentCollectionOption, 'id' | 'name' | 'parentCollectionId'>>,
  options?: { methods?: Set<string> },
): Promise<AgentRequestOption[]> {
  if (!projectId || collectionId === '' || collectionId == null) return [];

  const labelById = new Map(
    collections.map((c) => [c.id, c.name] as const),
  );

  if (collectionId === APPLY_ENV_SCOPE_ALL) {
    const raw = await apiService.getRequestsByProject(projectId);
    return mapAgentRequestOptions(raw, {
      methods: options?.methods,
      collectionLabelById: labelById,
      prefixCollectionName: true,
    });
  }

  const scopeId = toPositiveId(collectionId);
  if (scopeId == null) return [];
  const raw = await apiService.getRequestsByCollection(scopeId);
  return mapAgentRequestOptions(raw, { methods: options?.methods });
}
